import { combineRefs } from '@visiba/react-std';
import { type ForwardedRef, forwardRef } from 'react';
import { Controller, type RegisterOptions } from 'react-hook-form';
import { useText } from '../../../utils/hooks/use_text';
import { useFormContext } from '../form';
import { InputPropsHelper } from '../form/form.helpers';
import { CellulaInputSwitchRoot, type InputSwitchRootProps } from './_input_switch_root.component';

interface InputSwitchProps extends InputSwitchRootProps {
	name?: string;
	options?: RegisterOptions;
}

function CellulaInputSwitch(props: InputSwitchProps, ref: ForwardedRef<HTMLButtonElement>): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const formContext = useFormContext();
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const staticTexts = useText();

	if (formContext == null || props.name == null) {
		return <CellulaInputSwitchRoot {...props} ref={ref} />;
	}

	return (
		<Controller
			control={formContext.control.control}
			name={props.name}
			defaultValue={props.defaultValue}
			rules={{
				...props.options,
				onChange: props.onChange,
				onBlur: props.onBlur,
			}}
			disabled={props.disabled}
			render={({ field, fieldState, formState }) => {
				const inputPropsHelper = new InputPropsHelper(props, staticTexts, fieldState, formState);

				function handleCheckedChange(checked: boolean): void {
					if (props.readOnly) {
						return;
					}

					field.onChange(checked);
				}

				return (
					<CellulaInputSwitchRoot
						{...props}
						{...field}
						required={inputPropsHelper.isRequired()}
						invalid={inputPropsHelper.isInvalid()}
						invalidText={inputPropsHelper.invalidText()}
						readOnly={inputPropsHelper.isReadOnly()}
						onCheckedChange={handleCheckedChange}
						checked={!!field.value}
						ref={combineRefs([ref, field.ref])}
					/>
				);
			}}
		/>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A control that allows the user to toggle between checked and not checked.
 */

const InputSwitch = forwardRef(CellulaInputSwitch);

export type { InputSwitchProps };
export { InputSwitch };
